import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getMyProfileApi, updateMyLanguageApi, MyProfile } from "../apis/authApi";
import { Language } from "../state/preferencesStore";

/**
 * The authenticated user's language, read from and written to the server rather than
 * localStorage — so it follows them across devices, and so email we send is written in
 * the same language they read the app in.
 *
 * Resolution order is decided by the backend and returned as `effectiveLanguage`:
 * the user's own override, else their account's language, else "en". Clearing the
 * override (passing null) is a real state, not the same as picking "en": it means
 * "whatever the account says", including later changes to it.
 */
export function useLanguagePreference(enabled: boolean = true) {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();

  const { data: profile, isLoading } = useQuery({
    queryKey: ["myProfile"],
    queryFn: getMyProfileApi,
    enabled,
  });

  const { mutate, isPending: isSaving } = useMutation({
    mutationFn: updateMyLanguageApi,
    onSuccess: (result) => {
      // Patch the cache before refetching so the language switches immediately.
      queryClient.setQueryData(["myProfile"], (previous?: MyProfile) =>
        previous ? { ...previous, ...result } : previous
      );
      queryClient.invalidateQueries({ queryKey: ["myProfile"] });
    },
  });

  const effectiveLanguage = profile?.effectiveLanguage;

  // Apply the stored preference to i18n. Runs on load and after every save, which also
  // covers an admin changing the account language for users with no override.
  useEffect(() => {
    if (!effectiveLanguage || i18n.language === effectiveLanguage) return;
    i18n.changeLanguage(effectiveLanguage);
  }, [effectiveLanguage, i18n]);

  return {
    /** What the user sees, with the account fallback already applied. */
    language: effectiveLanguage ?? (i18n.language as Language),
    /** The user's own override, or null when they follow the account. */
    userLanguage: profile?.language ?? null,
    setLanguage: (language: Language | null) => mutate({ language }),
    isLoading,
    isSaving,
  };
}
